import React from 'react';
import PropTypes from 'prop-types';

/**
 * PulsingChevrons Component (Updated)
 * Displays 8 small SVG chevrons, now closer together,
 * that pulse in opacity consecutively at half speed (2s cycle).
 * Allows absolute positioning and orientation control via props.
 */
const PulsingChevrons = ({
  x,
  y,
  vertical = false,
  left = false,
  color = 'white',
  size = 80, // Represents desired width for the *original* spacing
  strokeWidth = 1,
}) => {

  // 1. Determine Rotation
  const rotation = vertical ? 90 : left ? 180 : 0;

  // 2. SVG Constants and Calculations
  const numChevrons = 8;
  const chevronWidth = 5;
  const chevronHeight = 8;
  // --- MODIFICATION: Make spacing half of what it would be for the given size ---
  const spacing = (size / numChevrons) / 2; // Halved spacing
  // --- MODIFICATION: Adjust total SVG width to match the new compressed spacing ---
  const svgWidth = spacing * numChevrons; // Total width occupied by compressed chevrons
  const svgHeight = chevronHeight + 2; // Chevron height + top/bottom padding

  // 3. Animation Constants
  // --- MODIFICATION: Double duration for half speed ---
  const animationDuration = 2; // seconds (was 1)
  const delayIncrement = animationDuration / numChevrons;

  // 4. Generate Chevron Elements
  const chevrons = Array.from({ length: numChevrons }).map((_, index) => {
    // Center chevron within its (now smaller) allocated space
    // Ensure padding calculation doesn't go negative if spacing is very small
    const horizontalPadding = Math.max(0, (spacing - chevronWidth) / 2);
    const startX = index * spacing + horizontalPadding;
    const midX = startX + chevronWidth;
    const topY = 1;
    const midY = topY + chevronHeight / 2;
    const bottomY = topY + chevronHeight;

    const points = `${startX},${topY} ${midX},${midY} ${startX},${bottomY}`;

    const style = {
      opacity: 0.2,
      // --- Reference updated animation duration ---
      animation: `pulseOpacity ${animationDuration}s infinite`,
      // --- Delay calculation automatically adjusts ---
      animationDelay: `${index * delayIncrement}s`,
    };

    return (
      <polyline
        key={index}
        points={points}
        style={style}
      />
    );
  });

  // 5. Style for the Wrapper Div
  const wrapperStyle = {
    position: 'absolute',
    left: `${x}%`,
    top: `${y}%`,
    // --- MODIFICATION: Use the new calculated svgWidth ---
    width: `${svgWidth}px`,
    height: `${svgHeight}px`,
    transformOrigin: 'center center',
    transform: `translate(-50%, -50%) rotate(${rotation}deg)`,
  };

  // 6. CSS Keyframes Definition (Remains the same)
  const keyframes = `
    @keyframes pulseOpacity {
      0% { opacity: 0.2; }
      50% { opacity: 1.0; }
      100% { opacity: 0.2; }
    }
  `;

  // 7. Render the Component
  return (
    <div style={wrapperStyle}>
      <style>{keyframes}</style>
      <svg
        // --- MODIFICATION: Use the new calculated svgWidth ---
        width={svgWidth}
        height={svgHeight}
        // --- MODIFICATION: Update viewBox width ---
        viewBox={`0 0 ${svgWidth} ${svgHeight}`}
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{ display: 'block' }}
      >
        <title>Pulsing Chevrons Indicator</title>
        {chevrons}
      </svg>
    </div>
  );
};

// PropTypes remain the same
PulsingChevrons.propTypes = {
  x: PropTypes.number.isRequired,
  y: PropTypes.number.isRequired,
  vertical: PropTypes.bool,
  left: PropTypes.bool,
  color: PropTypes.string,
  size: PropTypes.number, // Note: Size now influences density more directly
  strokeWidth: PropTypes.number,
};

export default PulsingChevrons;